import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import { Card } from "./ui/card";
import { Banknote, CalendarDays } from "lucide-react";

const METHODS = ["Cash", "EFT", "Card", "Combined", "Other"];
const ALLOWED_ROLES = ["platform_owner", "vendor_owner", "admin", "manager"];

export default function DailyPaymentSummary({ role }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    api.get(`/reports/daily-payments`, { params: { date } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Unable to load"));
  }, [date]);

  if (!ALLOWED_ROLES.includes(role)) return null;

  return (
    <Card className="p-6 mt-6 border-border shadow-sm" data-testid="daily-payment-summary">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl flex items-center gap-2">
          <Banknote className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Daily Payment Summary
        </h3>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <input
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-sm"
            data-testid="daily-payment-date"
          />
        </div>
      </div>
      {error && (
        <div className="text-sm text-destructive" data-testid="daily-payment-error">{error}</div>
      )}
      {!data && !error && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {data && (
        <div className="space-y-1.5">
          {METHODS.map((m) => (
            <div key={m} className="flex items-center justify-between text-sm"
                 data-testid={`daily-payment-${m.toLowerCase()}`}>
              <span className="text-muted-foreground">{m}</span>
              <span className="tabular-nums font-medium">{currency(data.methods?.[m] || 0)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-border"
               data-testid="daily-payment-total">
            <span className="font-medium">Total Sales</span>
            <span className="tabular-nums font-display text-lg">{currency(data.total_sales)}</span>
          </div>
          <div className="text-[11px] text-muted-foreground pt-1" data-testid="daily-payment-count">
            {data.sale_count} completed sale{data.sale_count === 1 ? "" : "s"} · voided excluded
          </div>
        </div>
      )}
    </Card>
  );
}
